import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface TrialUser {
  id: string
  email: string
  full_name: string
  primary_role: string
  preferred_language: string
  goals: string[]
  experience: string
  trial_expires_at: string
  access_token: string
  school_id?: string | null
  school_type?: string | null
}

interface TrialAuthState {
  trialUser: TrialUser | null
  isTrialMode: boolean
  hasHydrated: boolean
  setTrialUser: (user: TrialUser) => void
  clearTrialUser: () => void
  isTrialActive: () => boolean
  setHasHydrated: (hasHydrated: boolean) => void
}

export const useTrialAuth = create<TrialAuthState>()(
  persist(
    (set, get) => ({
      trialUser: null,
      isTrialMode: false,
      hasHydrated: false,

      setTrialUser: (user: TrialUser) => {
        const newState = {
          trialUser: user,
          isTrialMode: true
        }

        set(newState)

        // Force immediate localStorage update
        const storageKey = 'trial-auth-storage'
        const dataToStore = {
          state: newState,
          version: 0
        }

        try {
          localStorage.setItem(storageKey, JSON.stringify(dataToStore))
          // Verify it was stored
          const verification = localStorage.getItem(storageKey)
        } catch (error) {
          console.error('[TrialAuth] Failed to force localStorage update:', error)
        }
      },

      clearTrialUser: () => {
        set({
          trialUser: null,
          isTrialMode: false
        })
      },

      setHasHydrated: (hasHydrated: boolean) => {
        set({ hasHydrated })
      },

      isTrialActive: () => {
        const { trialUser } = get()
        if (!trialUser) {
          return false
        }

        const expiresAt = new Date(trialUser.trial_expires_at)
        const isActive = new Date() < expiresAt
        return isActive
      }
    }),
    {
      name: 'trial-auth-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true)
        }
      },
    }
  )
)
